import React, { useState, useRef, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import CountryCodeDropdown from './CountryCodeDropdown';
import CountryDropdown from './CountryDropdown';
import ItemTypeDropdown from './ItemTypeDropdown';

export default function LostItemRegistration() {
  const { t } = useTranslation();
  const [itemType, setItemType] = useState('');
  const [itemName, setItemName] = useState('');
  const [dateLost, setDateLost] = useState('');
  const [locationLost, setLocationLost] = useState('');
  const [description, setDescription] = useState('');
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [country, setCountry] = useState('');
  const [stayDateFrom, setStayDateFrom] = useState('');
  const [stayDateUntil, setStayDateUntil] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedItemId, setSubmittedItemId] = useState(null);
  const [images, setImages] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isStaffOrAdmin, setIsStaffOrAdmin] = useState(false);
  const { hotelId } = useParams();
  
  // Check if user is staff or admin
  useEffect(() => {
    async function checkUserRole() {
      if (currentUser) {
        const idTokenResult = await currentUser.getIdTokenResult();
        const isAdmin = idTokenResult.claims.admin === true;
        const isStaff = idTokenResult.claims.staff === true;
        setIsStaffOrAdmin(isAdmin || isStaff);
      }
    }
    checkUserRole();
  }, [currentUser]);

  // Counter for cycling through test data variations
  const testDataCounter = useRef(0);

  // Test data variations
  const testDataVariations = [
    // Variation 1: Smartphone
    {
      itemType: 'Phone',
      itemName: 'iPhone 14 Pro',
      dateLost: '2025-11-15',
      locationLost: 'Hotel Lobby',
      description: 'Black iPhone with a blue case. Lock screen has a picture of a mountain.',
      firstName: 'John',
      surname: 'Smith',
      countryCode: '+1',
      phoneNumber: '555-123-4567',
      country: 'United States',
      stayDateFrom: '2025-11-13',
      stayDateUntil: '2025-11-16',
      imageUrl: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8aXBob25lfGVufDB8fDB8fHww'
    },
    
    // Variation 2: Wallet
    {
      itemType: 'Wallet',
      itemName: 'Leather Bifold Wallet',
      dateLost: '2025-12-03',
      locationLost: 'Restaurant',
      description: 'Brown leather wallet containing ID, credit cards, and about $50 cash.',
      firstName: 'Maria',
      surname: 'Garcia',
      countryCode: '+34',
      phoneNumber: '612-345-678',
      country: 'Spain',
      stayDateFrom: '2025-12-01',
      stayDateUntil: '2025-12-05',
      imageUrl: 'https://images.unsplash.com/photo-1556741533-6e6a62bd8b49?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8d2FsbGV0fGVufDB8fDB8fHww'
    },
    
    // Variation 3: Jewelry
    {
      itemType: 'Jewelry',
      itemName: 'Diamond Earrings',
      dateLost: '2025-10-18',
      locationLost: 'Swimming Pool Area',
      description: 'Pair of small diamond stud earrings in white gold setting. High sentimental value.',
      firstName: 'Sarah',
      surname: 'Johnson',
      countryCode: '+44',
      phoneNumber: '7700-900123',
      country: 'United Kingdom',
      stayDateFrom: '2025-10-16',
      stayDateUntil: '2025-10-20',
      imageUrl: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8ZWFycmluZ3N8ZW58MHx8MHx8fDA%3D'
    },
    
    // Variation 4: Electronics
    {
      itemType: 'Headphones',
      itemName: 'AirPods Pro',
      dateLost: '2025-09-25',
      locationLost: 'Gym',
      description: 'White AirPods Pro in charging case. Case has a small scratch on the front.',
      firstName: 'Li',
      surname: 'Wei',
      countryCode: '+86',
      phoneNumber: '131-2345-6789',
      country: 'China',
      stayDateFrom: '2025-09-23',
      stayDateUntil: '2025-09-27',
      imageUrl: 'https://images.unsplash.com/photo-1572569511254-d8f925fe2cbb?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8YWlycG9kc3xlbnwwfHwwfHx8MA%3D%3D'
    },
    
    // Variation 5: Personal Item
    {
      itemType: 'Sunglasses',
      itemName: 'Designer Sunglasses',
      dateLost: '2025-08-12',
      locationLost: 'Beach Bar',
      description: 'Ray-Ban Wayfarer sunglasses, black frame with polarized lenses.',
      firstName: 'Alex',
      surname: 'Kim',
      countryCode: '+82',
      phoneNumber: '10-1234-5678',
      country: 'South Korea',
      stayDateFrom: '2025-08-10',
      stayDateUntil: '2025-08-14',
      imageUrl: 'https://images.unsplash.com/photo-1577803645773-f96470509666?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8c3VuZ2xhc3Nlc3xlbnwwfHwwfHx8MA%3D%3D'
    }
  ];

  // Function to populate form with test data
  function populateTestData() {
    // Get the current variation and increment counter for next click
    const currentVariation = testDataVariations[testDataCounter.current];
    testDataCounter.current = (testDataCounter.current + 1) % testDataVariations.length;
    
    // Set all form fields with the current variation data
    setItemType(currentVariation.itemType);
    setItemName(currentVariation.itemName);
    setDateLost(currentVariation.dateLost);
    setLocationLost(currentVariation.locationLost);
    setDescription(currentVariation.description);
    setFirstName(currentVariation.firstName);
    setSurname(currentVariation.surname);
    setCountryCode(currentVariation.countryCode);
    setPhoneNumber(currentVariation.phoneNumber);
    setCountry(currentVariation.country);
    setStayDateFrom(currentVariation.stayDateFrom);
    setStayDateUntil(currentVariation.stayDateUntil);
    
    // Set the image preview if available
    if (currentVariation.imageUrl) {
      setImageUrls([currentVariation.imageUrl]);
      // Create a dummy File object for the image
      fetch(currentVariation.imageUrl)
        .then(response => response.blob())
        .then(blob => {
          const file = new File([blob], 'test-image.jpg', { type: 'image/jpeg' });
          setImages([file]);
        })
        .catch(error => console.error('Error loading test image:', error));
    }
  }

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Append new files to existing ones instead of replacing
    const newImages = [...images, ...files];
    setImages(newImages);
    
    // Create preview URLs for new files and append to existing URLs
    const newUrls = files.map(file => URL.createObjectURL(file));
    const allUrls = [...imageUrls, ...newUrls];
    setImageUrls(allUrls);
    
    // Clear the file input so the same file can be selected again if needed
    e.target.value = '';
  };

  const handleRemoveImage = (index) => {
    const newImages = [...images];
    const newUrls = [...imageUrls];
    
    newImages.splice(index, 1);
    newUrls.splice(index, 1);
    
    setImages(newImages);
    setImageUrls(newUrls);
  };

  const uploadImages = async (itemId) => {
    const uploadedUrls = [];
    
    for (const image of images) {
      // Create a unique filename including hotelId
      const filename = `${Date.now()}-${image.name}`;
      // Include hotelId in the storage path
      const storageRef = ref(storage, `lost-items/${hotelId}/${itemId}/${filename}`); 
      
      try {
        // Upload the image
        await uploadBytes(storageRef, image);
        
        // Get the download URL
        const url = await getDownloadURL(storageRef);
        uploadedUrls.push(url);
      } catch (error) {
        console.error('Error uploading image:', error);
        throw error;
      }
    }
    
    return uploadedUrls;
  };

  // Country mapping for auto-sync
  const countryCodeToCountryMap = {
    '+1': 'United States', // Default to US for +1
    '+44': 'United Kingdom',
    '+33': 'France',
    '+49': 'Germany',
    '+39': 'Italy',
    '+34': 'Spain',
    '+31': 'Netherlands',
    '+32': 'Belgium',
    '+41': 'Switzerland',
    '+43': 'Austria',
    '+45': 'Denmark',
    '+46': 'Sweden',
    '+47': 'Norway',
    '+358': 'Finland',
    '+351': 'Portugal',
    '+30': 'Greece',
    '+48': 'Poland',
    '+420': 'Czech Republic',
    '+36': 'Hungary',
    '+81': 'Japan',
    '+82': 'South Korea',
    '+86': 'China',
    '+91': 'India',
    '+65': 'Singapore',
    '+852': 'Hong Kong',
    '+853': 'Macau',
    '+886': 'Taiwan',
    '+66': 'Thailand',
    '+60': 'Malaysia',
    '+62': 'Indonesia',
    '+63': 'Philippines',
    '+84': 'Vietnam',
    '+61': 'Australia',
    '+64': 'New Zealand',
    '+55': 'Brazil',
    '+52': 'Mexico',
    '+54': 'Argentina',
    '+56': 'Chile',
    '+57': 'Colombia',
    '+51': 'Peru',
    '+27': 'South Africa',
    '+20': 'Egypt',
    '+971': 'UAE',
    '+966': 'Saudi Arabia',
    '+972': 'Israel',
    '+90': 'Turkey',
    '+7': 'Russia',
  };

  // Auto-sync country when country code changes
  const handleCountryCodeChange = (newCountryCode) => {
    setCountryCode(newCountryCode);
    
    // Auto-set country if we have a mapping
    const mappedCountry = countryCodeToCountryMap[newCountryCode];
    if (mappedCountry) {
      setCountry(mappedCountry);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Form validation
    if (!itemType || !itemName || !dateLost || !locationLost || !firstName || !surname) {
      setError(t('lostItemRegistration.errors.requiredFields'));
      setLoading(false);
      return;
    }

    // Email validation
    if (email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError(t('lostItemRegistration.errors.invalidEmail'));
        setLoading(false);
        return;
      }
    }

    await handleProhibitedItemsAcknowledged();
    return;
  };

  const handleProhibitedItemsAcknowledged = async () => {
    try {
      setLoading(true);
      
      // Add document to Firestore including hotelId
      const itemData = {
        hotelId,
        itemType,
        itemName,
        dateLost,
        locationLost,
        description,
        firstName,
        surname,
        email,
        phone: countryCode && phoneNumber ? `${countryCode} ${phoneNumber}` : '',
        country,
        status: 'pending',
        createdAt: serverTimestamp(),
        matchId: null,
        userId: currentUser?.uid || null,
        viewed: false,
        stayDateFrom,
        stayDateUntil,
      };
      
      console.log('Creating lost item with viewed:', itemData.viewed);
      const docRef = await addDoc(collection(db, 'lost-items'), itemData);
      console.log('Lost item registered with ID:', docRef.id, 'and viewed status:', itemData.viewed);

      // Handle image upload
      if (images.length > 0) {
        setUploadingImages(true);
        const imageUrls = await uploadImages(docRef.id);
        setUploadingImages(false);
        
        // Update document with image URLs
        await updateDoc(docRef, {
          imageUrls: imageUrls
        });
      }

      // Reset form
      setItemType('');
      setItemName('');
      setDateLost('');
      setLocationLost('');
      setDescription('');
      setFirstName('');
      setSurname('');
      setEmail('');
      setCountryCode('');
      setPhoneNumber('');
      setCountry('');
      setStayDateFrom('');
      setStayDateUntil('');
      setImages([]);
      setImageUrls([]);
      setError('');
      
      // Navigate to admin page for staff/admin users, otherwise show success message
      if (isStaffOrAdmin) {
        navigate(`/${hotelId}/admin`);
      } else {
        setSubmitted(true);
        setSubmittedItemId(docRef.id);
      }
    } catch (error) {
      console.error('Error adding document: ', error);
      setError(t('lostItemRegistration.errors.submitFailed'));
    }

    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="lost-item-registration-container">
        <h2>{t('lostItemRegistration.successTitle')}</h2>
        <div className="success-message">
          <p>{t('lostItemRegistration.successMessage')}</p>
          <button onClick={() => navigate(`/${hotelId}/lost-items/${submittedItemId}`)}>
            {t('lostItemRegistration.buttons.viewReport')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="lost-item-registration-container">
      <h2>{t('lostItemRegistration.title')}</h2>
      <p className="required-fields-note">{t('lostItemRegistration.requiredFieldsNote')}</p>
      {error && <div className="error">{error}</div>}
      
      <div className="test-button-container">
        <button 
          type="button" 
          onClick={populateTestData} 
          className="test-button"
        >
          {t('lostItemRegistration.buttons.fillTestData')} ({testDataCounter.current + 1}/5)
        </button>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>{t('lostItemRegistration.sections.itemDetails')}</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label className="required">{t('lostItemRegistration.fields.itemType')}</label>
              <ItemTypeDropdown
                value={itemType}
                onChange={setItemType}
                placeholder={t('lostItemRegistration.placeholders.itemType')}
              />
            </div>
            
            <div className="form-group">
              <label className="required">{t('lostItemRegistration.fields.itemNameBrand')}</label>
              <input
                type="text"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                required
                placeholder={t('lostItemRegistration.placeholders.itemName')}
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label className="required">{t('lostItemRegistration.fields.dateLost')}</label>
              <div className="date-input-container">
                <input
                  type="date"
                  value={dateLost}
                  onChange={(e) => setDateLost(e.target.value)}
                  required
                />
                {dateLost && (
                  <button
                    type="button"
                    className="clear-date-button"
                    onClick={() => setDateLost('')}
                    title="Clear date"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            
            <div className="form-group">
              <label className="required">{t('lostItemRegistration.fields.locationLost')}</label>
              <input
                type="text"
                value={locationLost}
                onChange={(e) => setLocationLost(e.target.value)}
                required
                placeholder={t('lostItemRegistration.placeholders.locationLost')}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>{t('lostItemRegistration.fields.description')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('lostItemRegistration.placeholders.description')}
            />
          </div>

          <div className="form-group">
            <label>{t('lostItemRegistration.fields.photos')}</label>
            <div className="image-upload-container">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageChange}
                accept="image/*"
                multiple
                style={{ display: 'none' }}
              />
              {imageUrls.length === 0 ? (
                <div className="upload-area">
                  <div className="upload-icon" style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>📷</div>
                  <button
                    type="button"
                    className="upload-button"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <span style={{ fontSize: '1.2em' }}>📷</span>
                    {t('lostItemRegistration.buttons.selectImages')}
                  </button>
                  <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                    {t('lostItemRegistration.help.dragDrop', 'Drag and drop images here or click to browse')}
                  </p>
                </div>
              ) : (
                <div>
                  <button
                    type="button"
                    className="upload-button"
                    onClick={() => fileInputRef.current.click()}
                    style={{ marginBottom: '1rem' }}
                  >
                    <span style={{ fontSize: '1.2em' }}>📷</span>
                    {t('lostItemRegistration.buttons.addMoreImages', 'Add More Images')}
                  </button>
                </div>
              )}
              
              <div className="image-preview-container">
                {imageUrls.map((url, index) => (
                  <div key={index} className="image-preview">
                    <img src={url} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={() => handleRemoveImage(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h3>{t('lostItemRegistration.sections.contactInformation')}</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label className="required">{t('lostItemRegistration.fields.firstName')}</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label className="required">{t('lostItemRegistration.fields.surname')}</label>
              <input
                type="text"
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                required
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label className="required">{t('lostItemRegistration.fields.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="required">{t('lostItemRegistration.fields.phoneNumber')}</label>
              <div className="phone-input-container">
                <CountryCodeDropdown
                  value={countryCode}
                  onChange={handleCountryCodeChange}
                  placeholder={t('lostItemRegistration.placeholders.countryCode')}
                />
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                  placeholder={t('lostItemRegistration.placeholders.phoneNumber')}
                  className="phone-number-input"
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('lostItemRegistration.fields.country')}</label>
              <CountryDropdown
                value={country}
                onChange={setCountry}
                placeholder={t('lostItemRegistration.placeholders.country')}
              />
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h3>{t('lostItemRegistration.sections.stayInformation')}</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>{t('lostItemRegistration.fields.stayDateFrom')}</label>
              <div className="date-input-container">
                <input
                  type="date"
                  value={stayDateFrom}
                  onChange={(e) => setStayDateFrom(e.target.value)}
                  placeholder={t('lostItemRegistration.placeholders.stayDateFrom')}
                />
                {stayDateFrom && (
                  <button
                    type="button"
                    className="clear-date-button"
                    onClick={() => setStayDateFrom('')}
                    title="Clear date"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            
            <div className="form-group">
              <label>{t('lostItemRegistration.fields.stayDateUntil')}</label>
              <div className="date-input-container">
                <input
                  type="date"
                  value={stayDateUntil}
                  onChange={(e) => setStayDateUntil(e.target.value)}
                  placeholder={t('lostItemRegistration.placeholders.stayDateUntil')}
                />
                {stayDateUntil && (
                  <button
                    type="button"
                    className="clear-date-button"
                    onClick={() => setStayDateUntil('')}
                    title="Clear date"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="form-actions">
          <button
            type="submit"
            disabled={loading || uploadingImages}
          >
            {uploadingImages ? t('lostItemRegistration.buttons.uploadingImages') : t('lostItemRegistration.buttons.submitReport')}
          </button>
        </div>
      </form>

    </div>
  );
} 
